import { emptyStorePageContent } from "../store-page/content-limits";
import type { StorePageEditResult } from "../store-page/types";

import type { Action, Draft, DraftState, LastAction } from "./draft-model";

export const initialDraftState: DraftState = {
  draft: null,
  conflict: null,
  lastAction: null,
};

export function draftIsDirty(draft: Draft | null): boolean {
  return (
    draft !== null &&
    JSON.stringify(draft.content) !== JSON.stringify(draft.savedContent)
  );
}

function acceptSavedDraft({
  state,
  result,
  editedSinceSubmit,
}: {
  readonly state: DraftState;
  readonly result: StorePageEditResult;
  readonly editedSinceSubmit: boolean;
}): DraftState {
  const acknowledged = { ...emptyStorePageContent, ...result.content };
  const draft = editedSinceSubmit
    ? {
        content: state.draft?.content ?? acknowledged,
        savedContent: acknowledged,
        baseRevisionId: result.revision.id,
      }
    : null;
  return { draft, conflict: null, lastAction: "saved" };
}

function acceptRevision({
  state,
  operation,
  editedSinceSubmit,
}: {
  readonly state: DraftState;
  readonly operation: "approve" | "reject" | "revert";
  readonly editedSinceSubmit: boolean;
}): DraftState {
  const draft =
    operation === "revert" && !editedSinceSubmit ? null : state.draft;
  let lastAction: LastAction = null;

  if (operation === "approve") lastAction = "published";

  if (operation === "revert") lastAction = "reverted";

  return { ...state, draft, lastAction };
}

export function draftReducer(state: DraftState, action: Action): DraftState {
  switch (action.type) {
    case "edit": {
      const draft = state.draft ?? action.initial;
      const patch =
        typeof action.patch === "function"
          ? action.patch(draft.content)
          : action.patch;
      return {
        ...state,
        draft: { ...draft, content: { ...draft.content, ...patch } },
        lastAction: null,
      };
    }
    case "discard":
      return { ...state, draft: null, conflict: null };
    case "conflict":
      return {
        ...state,
        draft: state.draft ?? action.initial,
        conflict: action.conflict,
      };
    case "saved":
      return acceptSavedDraft({
        state,
        result: action.result,
        editedSinceSubmit: action.editedSinceSubmit,
      });
    case "revision":
      return acceptRevision({
        state,
        operation: action.operation,
        editedSinceSubmit: action.editedSinceSubmit,
      });
    case "keep-mine":
      return {
        ...state,
        conflict: null,
        draft: {
          content: state.draft?.content ?? action.savedContent,
          savedContent: {
            ...emptyStorePageContent,
            ...action.conflict.current_content,
          },
          baseRevisionId: action.conflict.current_revision_id,
        },
      };
    case "refreshed":
      return {
        ...state,
        draft: draftIsDirty(state.draft) ? state.draft : null,
      };
    case "last-action":
      return { ...state, lastAction: action.action };
  }
}
